import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { MousePointer2, BookOpen, Scissors, Monitor, Menu, AlertTriangle, ClipboardList, Euro } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

function NavLinks({ onNavigate }) {
  const loc = useLocation();
  const active = (p) => loc.pathname === p || (p === '/' && loc.pathname.startsWith('/runs'));
  const cls = (p) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
      active(p) ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
    }`;
  return (
    <nav className="p-3 space-y-1">
      <Link to="/" data-testid="nav-recipes" onClick={onNavigate} className={cls('/')}>
        <BookOpen className="h-4 w-4 shrink-0" /> Rotinas de recolha
      </Link>
      <Link to="/recorder" data-testid="nav-recorder" onClick={onNavigate} className={cls('/recorder')}>
        <MousePointer2 className="h-4 w-4 shrink-0" /> Nova gravação
      </Link>
      <Link to="/cutting-simulator" data-testid="nav-cutting-simulator" onClick={onNavigate} className={cls('/cutting-simulator')}>
        <Scissors className="h-4 w-4 shrink-0" /> Máquina de Corte
      </Link>
      <Link to="/ordens-fabrico" data-testid="nav-ordens-fabrico" onClick={onNavigate} className={cls('/ordens-fabrico')}>
        <ClipboardList className="h-4 w-4 shrink-0" /> Ordens de Fabrico
      </Link>
      <Link to="/custos" data-testid="nav-custos" onClick={onNavigate} className={cls('/custos')}>
        <Euro className="h-4 w-4 shrink-0" /> Custos
      </Link>
      <Link to="/anomalias" data-testid="nav-anomalias" onClick={onNavigate} className={cls('/anomalias')}>
        <AlertTriangle className="h-4 w-4 shrink-0" /> Anomalias
      </Link>
      <Link to="/tv" data-testid="nav-tv" onClick={onNavigate} className={cls('/tv')}>
        <Monitor className="h-4 w-4 shrink-0" /> TV (Modo Ecrã)
      </Link>
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-3" data-testid="brand-logo">
      <div className="rounded-xl bg-slate-900 px-3 py-2 shrink-0">
        <img
          src={`${process.env.PUBLIC_URL || ""}/samidel-logo-white.png`}
          alt="Samidel · textile manufacturing"
          className="h-8 w-auto select-none"
          draggable={false}
        />
      </div>
      <div className="min-w-0 hidden sm:block lg:hidden">
        <div className="text-xs font-semibold tracking-tight truncate" style={{ fontFamily: 'Space Grotesk' }}>
          Gestão de Corte
        </div>
        <div className="text-[11px] text-muted-foreground truncate">Rex 50 ES</div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();

  // Close mobile drawer when route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  return (
    <div className="min-h-dvh grid grid-cols-1 lg:grid-cols-[260px_1fr] bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col border-r border-border bg-card/60 backdrop-blur">
        <div className="px-6 py-5 border-b border-border">
          <Brand />
          <div className="mt-2 text-[11px] text-muted-foreground tracking-wide uppercase">Gestão de Corte · Rex 50 ES</div>
        </div>
        <NavLinks />
        <div className="mt-auto p-4 text-xs text-muted-foreground">
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <div className="font-medium text-foreground mb-1">Dica</div>
            Inicie uma sessão com qualquer URL, clique nos botões na captura de ecrã e adicione um passo “Extrair” na página que contém a tabela.
          </div>
        </div>
      </aside>

      {/* Main column with mobile top bar */}
      <div className="flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-card/80 backdrop-blur">
          <Brand />
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label="Abrir menu"
                data-testid="mobile-menu-btn"
                className="shrink-0"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0">
              <div className="px-6 py-5 border-b border-border">
                <Brand />
                <div className="mt-2 text-[11px] text-muted-foreground tracking-wide uppercase">Gestão de Corte · Rex 50 ES</div>
              </div>
              <NavLinks onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
        </header>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
